import L, { type LatLng, type Map as LeafletMap, type Layer, type FeatureGroup } from 'leaflet';
import { type MilSym, renderMultipoint, tacticalFeatureStyle, hasDrawableGeometry } from './renderer';

/**
 * Plain, serialisable snapshot of a drawn symbol — the persistence seam. Every
 * concrete symbol serialises to `{ sidc, points, …params }`.
 *
 * `sidc` is the discriminator: the APP-6D symbol id is the standards-based
 * identity of the graphic, so the symbol factory keys off it (see `registry.ts`)
 * rather than off a local nickname like `'attack-line'`. It is also the shape the
 * backend stores and the wire format carries, so no second mapping table is
 * needed in any of the three clients.
 *
 * `points` are the user-placed vertices, tail→tip, stored `[lng, lat]` to match
 * GeoJSON axis order — the same order the renderer's control-point string, the
 * `graphics.points` JSONB column and the CoT `points` attribute all use, so the
 * coordinates never need swapping anywhere along the path.
 *
 * Any remaining keys are symbol-family parameters (e.g. `width_m`), which ride
 * on the wire as `<ft>` attributes.
 */
export interface SerializedSymbol {
    sidc: string;
    points: [number, number][];
    [param: string]: unknown;
}

/**
 * Adapter between the drawing Tool and a concrete tactical-graphic
 * implementation. It owns the symbol's geometry (its points, tail→tip) and a
 * self-managed Leaflet container (`layer`) holding the rendered geometry. The
 * Tool mounts/unmounts that container but never touches geometry — so adding a
 * new graphic means adding a subclass, not changing the Tool.
 *
 * Subclasses supply the symbol-specific bits: the SIDC, the minimum point
 * count, how the points map to renderer control points, and how to serialise.
 * Everything mechanical (rendering, swapping the geometry layer) is shared here.
 */
export abstract class TacticalSymbol {
    /** User-placed vertices, tail→tip. */
    protected points: LatLng[] = [];

    /**
     * Self-owned container the Tool adds to its preview / finished group.
     *
     * A `FeatureGroup`, not a plain `LayerGroup`: a FeatureGroup re-fires its
     * children's events on itself, which is what lets a consumer bind one
     * `click` to the whole graphic (`GraphicsLayer` does, to select it for
     * deletion). With a LayerGroup those child clicks go nowhere and the
     * graphic is unselectable.
     */
    readonly layer: FeatureGroup = L.featureGroup();

    private geometry: Layer | null = null;

    /** Fewest points needed before the graphic can render. */
    abstract readonly minPoints: number;

    constructor(
        protected readonly map: LeafletMap,
        protected readonly milSym: MilSym,
        /**
         * APP-6D symbol id code for *this* graphic — the full code, including
         * the identity and status digits.
         *
         * Held per instance rather than as a subclass constant because a symbol
         * family is drawn in every affiliation: a hostile planned axis of advance
         * and a friendly present one are the same subclass and the same draw
         * rule, differing only in two digits that the renderer (and the colour
         * rule in `tacticalFeatureStyle`) read straight out of the code. A
         * constant here would have silently discarded the caller's variant and
         * drawn every line friendly.
         */
        readonly sidc: string,
    ) {}

    /** Replace the points (the Tool owns click collection and feeds it here). */
    setPoints(points: LatLng[]): void {
        this.points = [...points];
    }

    get isDrawable(): boolean {
        return this.points.length >= this.minPoints;
    }

    /**
     * Arrange the points (plus any synthesised points, e.g. a width point) into
     * the control-point order this SIDC's draw rule expects.
     */
    protected abstract controlPoints(points: LatLng[]): LatLng[];

    abstract serialize(): SerializedSymbol;

    /** Render live while drawing: the points with `cursor` as the provisional tip. */
    renderPreview(cursor: LatLng | null): void {
        this.renderGeometry(cursor ? [...this.points, cursor] : this.points);
    }

    /** Commit the current points as the finished geometry. */
    finalize(): void {
        this.renderGeometry(this.points);
    }

    /** Empty the container (used when an in-progress draw is discarded). */
    clear(): void {
        this.layer.clearLayers();
        this.geometry = null;
    }

    private renderGeometry(points: LatLng[]): void {
        if (this.geometry) {
            this.layer.removeLayer(this.geometry);
            this.geometry = null;
        }
        if (points.length < this.minPoints) return;
        const fc = renderMultipoint(this.milSym, this.sidc, this.controlPoints(points));
        if (!fc) return;
        const geometry = L.geoJSON(fc, {
            style: (feature) => tacticalFeatureStyle(feature, this.sidc),
            filter: hasDrawableGeometry,
        });
        geometry.addTo(this.layer);
        this.geometry = geometry;
    }
}
